import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Globe, Radio, ArrowRight, MapPin, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SyndicationWidget({ token }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/syndication/dashboard?limit=4`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (mounted) setData(res.data);
      } catch (e) {
        if (mounted) setData({ total_submissions: 0, by_status: {}, top_events: [], targets_per_event: 0 });
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [token]);

  if (loading) {
    return (
      <Card className="bg-card border-border" data-testid="syndication-widget-loading">
        <CardContent className="p-5 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading syndication progress…
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const bs = data.by_status || {};
  const totalActions = (bs.pending || 0) + (bs.submitted || 0) + (bs.live || 0);

  return (
    <Card className="bg-card border-border" data-testid="syndication-widget">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-indigo-500/10">
                <Globe className="h-5 w-5 text-indigo-500" />
              </div>
              <h3 className="font-heading text-lg font-semibold">Event Syndication</h3>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {data.targets_per_event} curated high-DA outlets per event · {data.emails_sent_total || 0} press emails sent across {data.blast_count || 0} blasts
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate('/admin/events')} className="text-xs" data-testid="syndication-widget-cta">
            Manage <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>

        {/* Status pill row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          <StatusPill label="Pending" value={bs.pending || 0} color="amber" testid="syn-pending" />
          <StatusPill label="Submitted" value={bs.submitted || 0} color="indigo" testid="syn-submitted" />
          <StatusPill label="Live" value={bs.live || 0} color="emerald" testid="syn-live" />
          <StatusPill label="Total" value={data.total_submissions || 0} color="violet" testid="syn-total" />
        </div>

        {/* Top events with progress */}
        {(data.top_events || []).length === 0 ? (
          <div className="text-center py-4 text-xs text-muted-foreground">
            No syndication activity yet. Open an event's Marketing tab to start distributing.
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider">Top events by syndication activity</p>
            {data.top_events.map(ev => {
              const p = ev.progress || {};
              const denom = data.targets_per_event || 1;
              const pct = Math.min(100, Math.round(((p.live || 0) + (p.submitted || 0)) / denom * 100));
              return (
                <div
                  key={ev.event_id}
                  onClick={() => navigate(`/admin/events/${ev.event_id}/marketing`)}
                  className="border border-border rounded-lg p-3 hover:border-indigo-500/40 cursor-pointer transition-colors"
                  data-testid={`syn-widget-event-${ev.event_id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="text-sm font-medium truncate max-w-[260px]">{ev.name}</p>
                      {ev.city && (
                        <Badge variant="outline" className="text-[10px] capitalize">
                          <MapPin className="h-2.5 w-2.5 mr-0.5" />{(ev.city || '').replace(/_/g, ' ')}
                        </Badge>
                      )}
                    </div>
                    <span className="text-[10px] font-mono text-muted-foreground">{pct}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-indigo-500 to-emerald-500" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="flex gap-3 mt-1.5 text-[10px] text-muted-foreground">
                    <span><span className="text-amber-500 font-semibold">{p.pending || 0}</span> pending</span>
                    <span><span className="text-indigo-400 font-semibold">{p.submitted || 0}</span> submitted</span>
                    <span><span className="text-emerald-500 font-semibold">{p.live || 0}</span> live</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {totalActions > 0 && (
          <div className="mt-3 pt-3 border-t border-border flex items-center gap-2 text-[11px] text-muted-foreground">
            <Radio className="h-3 w-3 text-emerald-500" />
            One-click press release blast available per event.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const StatusPill = ({ label, value, color, testid }) => (
  <div className={`p-2.5 rounded-lg border bg-${color}-500/5 border-${color}-500/20`} data-testid={testid}>
    <p className={`text-xl font-bold text-${color}-500`}>{value}</p>
    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
  </div>
);
